import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { Logger, TLogger } from '@logger/Logger';
import { BasePlaylistRotationControllerEvents } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.events';
import { IFragmentLoaderConfig } from '@streamLoader/FragmentLoader/FragmentLoader.config';
import { FragmentLoaderEvents } from '@streamLoader/FragmentLoader/FragmentLoader.events';
import { ELoadingState } from '@streamLoader/net/INetworkLoader';
import { NetworkLoader } from '@streamLoader/net/NetworkLoader';
import bind from '@utils/decorators/bind';

import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '../../types/common';

export class FragmentLoader extends ConfigConsumer<IFragmentLoaderConfig> implements IDebbugable {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public groups: string[] = ['loader'];
  public additionalInfo: TDebuggableAdditionalInfo = { loadingFragments: {} };

  private _lastRequestedFragments: Fragment[] = [];
  private _pendingFragments: Fragment[] = [];
  private _loaders: NetworkLoader[] = [];

  private readonly _logger: TLogger;
  private readonly _fragmentsDetectedSubscription: string;
  private readonly _fragmentAddedToBufferSubscription: string;
  private readonly _fragmentHistoryReadyToPrintSubscription: string;

  constructor(eventBus: TEventBus) {
    super('fragmentLoader', eventBus);

    this._logger = Logger(this._eventBus, 'Doppio - FragmentLoader');

    this._fragmentsDetectedSubscription = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.fragments.fragmentsDetected,
      this._onFragmentsDetected,
    );
    this._fragmentAddedToBufferSubscription = this._eventBus.subscribe(BufferControllerEvents.fragmentAdded, this._onFragmentAddedToBuffer);
    this._fragmentHistoryReadyToPrintSubscription = this._eventBus.subscribe(
      FragmentLoaderEvents.fragments.fragmentHistoryReadyForPrint,
      this._onFragmentHistoryReadyToPrint,
    );

    this._logger.info('Instance created');
  }

  public destroy(): void {
    super.destroy();

    this._loaders.forEach((loader) => {
      loader.destroy();
    });

    this._eventBus.unsubscribe(this._fragmentsDetectedSubscription);
    this._eventBus.unsubscribe(this._fragmentAddedToBufferSubscription);
    this._eventBus.unsubscribe(this._fragmentHistoryReadyToPrintSubscription);

    this._logger.info('Destroyed');
  }

  @bind
  public _handleConfigUpdated(): void {
    return;
  }

  @bind
  private _onFragmentsDetected(fragments: Fragment[]): void {
    this.status = 'Handling new fragments';
    this._logger.debug(
      `Fragments was requested to load`,
      fragments.map((fragment) => fragment.getMsnPn()),
    );

    const validFragments: Fragment[] = fragments.filter(
      (fragment) => !this._lastRequestedFragments.find((frag) => frag.url === fragment.url),
    );
    this._logger.verbose(
      `Actual fragments is`,
      validFragments.map((fragment) => fragment.getMsnPn()),
    );

    this._pendingFragments = this._pendingFragments.concat(validFragments);

    validFragments.forEach((fragment) => {
      this._eventBus.publish(FragmentLoaderEvents.fragments.fragmentLoadingStarted, fragment);

      this.additionalInfo.loadingFragments.url = ELoadingState.UNKNOWN;

      this._addFragmentToQueue(fragment.url, fragment.requestedLoadingTimeout, fragment);
    });

    this._lastRequestedFragments = fragments;
  }

  @bind
  private _onFragmentAddedToBuffer(fragment: Fragment): void {
    fragment.goToLifecycleStage('addedToBuffer');
  }

  @bind
  private _onFragmentLoadAborted(fragment: Fragment): void {
    fragment.goToLifecycleStage('loadDataAborted');
  }

  @bind
  private _onFragmentHistoryReadyToPrint(e: { historyString: string }): void {
    this._logger.info(e.historyString);
  }

  private _addFragmentToQueue(url: string, timeout: number | undefined = this.config?.fragmentLoadingTimeout, fragment: Fragment): void {
    this._logger.verbose(`Requesting ${url} to load; Fragment ${fragment.getMsnPn()}`);
    fragment.goToLifecycleStage('loadDataStart');
    const loader: NetworkLoader = new NetworkLoader(
      {
        url,
        autoLoad: true,
        mode: 'arrayBuffer',
        onStateChanged: this._onLoaderStateChanged,
        loadingTimeout: timeout,
        workerMode: this.config.workerMode,
      },
      this._eventBus,
    );

    this._loaders.push(loader);
  }

  @bind
  private _onLoaderStateChanged(_prevState: ELoadingState, nextState: ELoadingState, instance: NetworkLoader): void {
    this.additionalInfo.loadingFragments[instance.url] = nextState;

    if (![ELoadingState.LOADED, ELoadingState.ERROR, ELoadingState.ABORTED].includes(nextState)) {
      return;
    }

    this._logger.verbose(`Loader state changed: ${_prevState} -> ${nextState}`);

    const downloadedFragment: Fragment | undefined = this._pendingFragments.find((frag) => frag.url === instance.url);
    if (downloadedFragment) {
      downloadedFragment.stats = instance.stats;
      downloadedFragment.data = instance.data;
    }

    if (!downloadedFragment) {
      this._logger.warn(`Downloaded fragment was not even planned. Ignoring it`, { url: instance.url });
      return;
    }

    if (instance.error) {
      downloadedFragment.error = instance.error;
    }

    if (nextState === ELoadingState.LOADED) {
      this._logger.debug(`${instance.url} fragment downloaded (${downloadedFragment.getMsnPn()})`);
      downloadedFragment.goToLifecycleStage('loadDataEnd');

      delete this.additionalInfo.loadingFragments[instance.url];

      this._eventBus.publish(FragmentLoaderEvents.fragments.fragmentLoaded, downloadedFragment);
    }

    if (nextState === ELoadingState.ERROR) {
      this._lastRequestedFragments = this._lastRequestedFragments.filter((frag) => frag.url !== instance.url);

      this._logger.debug(`${instance.url} fragment loading error (${downloadedFragment.getMsnPn()})`);
      this._onFragmentLoadAborted(downloadedFragment);

      this._eventBus.publish(FragmentLoaderEvents.fragments.fragmentLoadingError, downloadedFragment);
    }

    if (nextState === ELoadingState.ABORTED) {
      this._lastRequestedFragments = this._lastRequestedFragments.filter((frag) => frag.url !== instance.url);

      this._logger.debug(`${instance.url} fragment loading aborted (${downloadedFragment.getMsnPn()})`);
      this._onFragmentLoadAborted(downloadedFragment);

      this._eventBus.publish(FragmentLoaderEvents.fragments.fragmentLoadingError, downloadedFragment);
    }

    this._pendingFragments = this._pendingFragments.filter((frag) => frag !== downloadedFragment);
    this._loaders = this._loaders.filter((loader) => loader !== instance);

    this.status = this._loaders.length ? 'Loading fragments' : 'Idle';
  }
}
